import { useCallback, useEffect, useState } from "react";
import { NavLink, Link, Outlet, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import axios from "axios";
import {
  LayoutDashboard, Users, ShoppingBag, Layers, FolderTree,
  LifeBuoy, Wrench, ScrollText, LogOut, Menu, X, ShieldCheck,
  CreditCard, Megaphone, PenLine, SlidersHorizontal, RotateCcw, KeyRound, Loader2, ArrowLeftRight, Receipt, Send, Banknote, FileText, MailCheck, TrendingUp, Gift, Package, FileSearch,
} from "lucide-react";
import aapi, { clearAdminToken, getAdminToken, formatApiError } from "./aapi";
import { inputCls } from "@/panel/components";
import { EASE } from "@/components/Motion";
import { Logo } from "@/components/Logo";

const NAV = [
  {
    group: "OVERVIEW",
    items: [{ to: "/admin", end: true, label: "Dashboard", icon: LayoutDashboard, testid: "admin-nav-dashboard" }],
  },
  {
    group: "MANAGE",
    items: [
      { to: "/admin/users", label: "Users", icon: Users, testid: "admin-nav-users" },
      { to: "/admin/orders", label: "Orders", icon: ShoppingBag, testid: "admin-nav-orders" },
      { to: "/admin/services", label: "Services", icon: Layers, testid: "admin-nav-services" },
      { to: "/admin/categories", label: "Categories", icon: FolderTree, testid: "admin-nav-categories" },
    ],
  },
  {
    group: "OPERATIONS",
    items: [
      { to: "/admin/support", label: "Support", icon: LifeBuoy, testid: "admin-nav-support" },
      { to: "/admin/payments", label: "Payments", icon: CreditCard, testid: "admin-nav-payments" },
      { to: "/admin/transfer", label: "Transfer Funds", icon: Banknote, testid: "admin-nav-transfer" },
      { to: "/admin/transactions", label: "Transactions", icon: ArrowLeftRight, testid: "admin-nav-transactions" },
      { to: "/admin/statements", label: "Statements", icon: FileText, testid: "admin-nav-statements" },
      { to: "/admin/product-profit", label: "Product Profit", icon: Package, testid: "admin-nav-product-profit" },
      { to: "/admin/analyzer", label: "Analyzer", icon: FileSearch, testid: "admin-nav-analyzer" },
      { to: "/admin/invoices", label: "Invoices", icon: Receipt, testid: "admin-nav-invoices" },
      { to: "/admin/revenue", label: "Revenue", icon: TrendingUp, testid: "admin-nav-revenue" },
      { to: "/admin/refunds", label: "Refunds", icon: RotateCcw, testid: "admin-nav-refunds" },
      { to: "/admin/broadcast", label: "Broadcast", icon: Megaphone, testid: "admin-nav-broadcast" },
      { to: "/admin/send-mail", label: "Send Mail", icon: Send, testid: "admin-nav-send-mail" },
      { to: "/admin/mail-log", label: "Mail History", icon: MailCheck, testid: "admin-nav-mail-log" },
      { to: "/admin/offers", label: "Offers", icon: Gift, testid: "admin-nav-offers" },
      { to: "/admin/maintenance", label: "Maintenance", icon: Wrench, testid: "admin-nav-maintenance" },
      { to: "/admin/audit", label: "Audit Log", icon: ScrollText, testid: "admin-nav-audit" },
    ],
  },
  {
    group: "WEBSITE",
    items: [
      { to: "/admin/content", label: "Content", icon: PenLine, testid: "admin-nav-content" },
      { to: "/admin/settings", label: "Settings", icon: SlidersHorizontal, testid: "admin-nav-settings" },
    ],
  },
];

const SidebarContent = ({ onNavigate }) => (
  <div className="flex h-full flex-col">
    <Link to="/admin" onClick={onNavigate} data-testid="admin-logo" className="flex items-center gap-3 px-6 py-6">
      <Logo className="h-10 w-10 rounded-xl object-cover ring-1 ring-gold/40" />
      <div>
        <div className="text-sm font-bold tracking-[0.18em] text-neutral-100">
          BEATKING<span className="text-gold">SMM</span>
        </div>
        <div className="font-mono text-[9px] tracking-[0.3em] text-gold/70">ADMIN CONSOLE</div>
      </div>
    </Link>
    <nav className="flex-1 overflow-y-auto px-4 pb-6">
      {NAV.map((g) => (
        <div key={g.group} className="mt-6 first:mt-2">
          <div className="px-3 pb-2 font-mono text-[9px] tracking-[0.3em] text-neutral-600">{g.group}</div>
          {g.items.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              onClick={onNavigate}
              data-testid={item.testid}
              className={({ isActive }) =>
                `mb-1 flex items-center gap-3 rounded-xl border px-3 py-2.5 text-sm transition-colors duration-300 ${
                  isActive
                    ? "border-gold/30 bg-gold/[0.08] font-semibold text-goldlight"
                    : "border-transparent text-neutral-400 hover:bg-white/[0.03] hover:text-neutral-100"
                }`
              }
            >
              <item.icon size={16} strokeWidth={1.9} />
              {item.label}
            </NavLink>
          ))}
        </div>
      ))}
    </nav>
    <div className="border-t border-white/5 p-4">
      <Link
        to="/panel"
        data-testid="admin-nav-customer-panel"
        className="flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm text-neutral-500 transition-colors hover:text-goldlight"
      >
        <ShieldCheck size={16} /> Customer Panel
      </Link>
    </div>
  </div>
);

export default function AdminLayout() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [maintenance, setMaintenance] = useState(null);
  const navigate = useNavigate();

  const refreshMaintenance = useCallback(async () => {
    const { data } = await aapi.get("/maintenance");
    setMaintenance(data);
  }, []);

  useEffect(() => {
    refreshMaintenance().catch(() => {});
    const iv = setInterval(() => refreshMaintenance().catch(() => {}), 30000);
    return () => clearInterval(iv);
  }, [refreshMaintenance]);

  const logout = () => {
    clearAdminToken();
    navigate("/admin/login");
  };

  return (
    <div className="min-h-screen bg-ink text-neutral-100">
      <aside className="fixed inset-y-0 left-0 z-40 hidden w-64 border-r border-white/5 bg-[#08080B] lg:block" data-testid="admin-sidebar">
        <SidebarContent onNavigate={() => {}} />
      </aside>

      <AnimatePresence>
        {menuOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-40 bg-black/70 backdrop-blur-sm lg:hidden"
              onClick={() => setMenuOpen(false)}
            />
            <motion.aside
              initial={{ x: -280 }}
              animate={{ x: 0 }}
              exit={{ x: -280 }}
              transition={{ duration: 0.4, ease: EASE }}
              className="fixed inset-y-0 left-0 z-50 w-64 border-r border-white/5 bg-[#08080B] lg:hidden"
              data-testid="admin-mobile-sidebar"
            >
              <SidebarContent onNavigate={() => setMenuOpen(false)} />
            </motion.aside>
          </>
        )}
      </AnimatePresence>

      <div className="lg:pl-64">
        <header className="sticky top-0 z-30 flex items-center justify-between gap-4 border-b border-white/5 bg-ink/85 px-4 py-4 backdrop-blur-xl sm:px-8">
          <div className="flex items-center gap-3">
            <button
              onClick={() => setMenuOpen(true)}
              data-testid="admin-menu-button"
              aria-label="Open menu"
              className="flex h-10 w-10 items-center justify-center rounded-xl border border-white/10 text-neutral-300 lg:hidden"
            >
              {menuOpen ? <X size={18} /> : <Menu size={18} />}
            </button>
            <span className="hidden font-mono text-[10px] tracking-[0.3em] text-neutral-600 sm:block">
              COMPLETE PLATFORM CONTROL
            </span>
          </div>
          <div className="flex items-center gap-3">
            {maintenance?.enabled && (
              <span className="flex items-center gap-2 rounded-full border border-amber-400/40 bg-amber-400/10 px-4 py-2 font-mono text-[10px] tracking-[0.15em] text-amber-300" data-testid="admin-maintenance-badge">
                <span className="relative flex h-1.5 w-1.5">
                  <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-amber-400 opacity-70" />
                  <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-amber-400" />
                </span>
                MAINTENANCE ON
              </span>
            )}
            <button
              onClick={logout}
              data-testid="admin-logout-button"
              aria-label="Log out"
              className="flex h-10 w-10 items-center justify-center rounded-full border border-white/10 text-neutral-400 transition-colors hover:border-rose-400/50 hover:text-rose-300"
            >
              <LogOut size={16} />
            </button>
          </div>
        </header>

        <main className="mx-auto max-w-7xl px-4 py-8 sm:px-8 sm:py-10" data-testid="admin-content">
          <Outlet context={{ maintenance, refreshMaintenance }} />
        </main>
      </div>
    </div>
  );
}
